/**
 * **A tab's section heading** — the `<h2>` every section on this screen opens with.
 *
 * Six of these are drawn across four tabs and they were six hand-written copies of one
 * `motion.div`, right down to `delay: 20` written out in each. The number is the point: it is a
 * decision about how the tab's sections enter, it belongs in one place, and six literals is six
 * chances for the seventh section to enter on a different beat.
 *
 * ── What it owns, and what the caller therefore stops knowing ─────────────────────────────────
 * The element types (`<h2>`, `<em>`), the classes, the entrance and its delay, and the rule that a
 * heading carrying a CONTROL gets a spacer while one carrying a SENTENCE gets a `flex: 1` caption.
 * A caller says what the section is called and what goes at its far end.
 *
 * ── `count` is a prop rather than a node, because it is the accent ────────────────────────────
 * "Wymaga reakcji · 3" is the one place the accent is spent on Przegląd. Passing it as a number is
 * what keeps the pill's shape and its ember here, where there is exactly one of it, rather than at
 * a call site that could spell it a second way.
 *
 * ── `reducedMotion` is required ───────────────────────────────────────────────────────────────
 * Never defaulted, never read from a hook here: `shared/design-system/motion`'s rule is that the
 * preference travels down from the screen that read it once, so a tab cannot forget to honour it.
 */

import type { ReactNode } from 'react';
import { motion } from 'motion/react';

import { screenMotion } from '~/shared/design-system/motion';

import styles from './SectionHead.module.css';

/** The beat every section heading on this screen enters on. One number, six headings. */
const HEAD_DELAY_MS = 20;

interface SectionHeadProps {
  /** The section's name. It becomes the `<h2>`, so it is also the tab's document outline. */
  readonly title: string;
  /** How many items in this section want a reply. Omitted where the question does not apply. */
  readonly count?: number;
  /** The sentence at the far end — a caption, a date, a window. Mutually exclusive with children. */
  readonly aside?: ReactNode;
  /** The viewer asked for reduced motion. See `shared/design-system/motion`. */
  readonly reducedMotion: boolean;
  /**
   * A CONTROL at the far end instead of a sentence. It is preceded by a spacer rather than being
   * stretched, which is the difference between a control at the end of a row and a control the
   * width of the row.
   */
  readonly children?: ReactNode;
  /**
   * `false` where the heading arrives INSIDE a hand-over that already is the entrance — the
   * Przegląd's main column, which is drawn whole on `gate.reveal` once its reads have answered,
   * because whether a task section exists at all decides where the table's heading stands. A heading
   * mounted with the answer and entering again on its own beat is the double entrance the loading
   * policy bans.
   */
  readonly enters?: boolean;
}

export function SectionHead({
  title,
  count,
  aside,
  reducedMotion,
  children,
  enters = true,
}: SectionHeadProps) {
  return (
    <motion.div
      className={styles.head}
      {...(enters ? screenMotion('enter', { reducedMotion, delay: HEAD_DELAY_MS }) : {})}
    >
      <h2 className={styles.title}>{title}</h2>
      {count === undefined ? null : <span className={styles.count}>{count}</span>}
      {aside === undefined ? null : <em className={styles.aside}>{aside}</em>}
      {children === undefined ? null : (
        <>
          <span className={styles.spacer} />
          {children}
        </>
      )}
    </motion.div>
  );
}
